import type { Entity, DailyMap, FlowNode, Allocation, Settings, EntityType, Side } from './types';

const TOKEN_KEY = 'coco_token';
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? '';
}
export function setToken(v: string): void {
  localStorage.setItem(TOKEN_KEY, v);
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const tok = getToken();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;
  const res = await fetch(`${API_BASE}/api${path}`, { ...init, headers, body: init?.body ? init.body : undefined });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export const api = {
  getEntities: () => req<{ entities: Entity[] }>('/entities').then((r) => r.entities),
  createEntity: (type: EntityType, name: string) => req<{ entity: Entity }>('/entities', { method: 'POST', body: JSON.stringify({ type, name }) }).then((r) => r.entity),
  deleteEntity: (id: string) => req('/entities/' + id, { method: 'DELETE' }),

  getOrCreateDailyMap: (date: string) => req<{ dailyMap: DailyMap }>(`/daily-maps/${encodeURIComponent(date)}`).then((r) => r.dailyMap),

  getNodes: (mapId: string) => req<{ nodes: FlowNode[] }>(`/nodes/${mapId}`).then((r) => r.nodes),
  createNode: (data: { dailyMapId: string; side: Side; entityId: string; totalAmount: number; metadataJson?: string | null }) =>
    req<{ node: FlowNode }>('/nodes', { method: 'POST', body: JSON.stringify(data) }).then((r) => r.node),
  updateNode: (id: string, patch: { totalAmount?: number; metadataJson?: string | null }) =>
    req<{ node: FlowNode }>(`/nodes/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }).then((r) => r.node),
  deleteNode: (id: string) => req(`/nodes/${id}`, { method: 'DELETE' }),

  getAllocations: (mapId: string) => req<{ allocations: Allocation[] }>(`/allocations/${mapId}`).then((r) => r.allocations),
  createAllocation: (data: { dailyMapId: string; sourceNodeId: string; targetNodeId: string; amount: number; note?: string }) =>
    req<{ allocation: Allocation }>('/allocations', { method: 'POST', body: JSON.stringify(data) }).then((r) => r.allocation),
  deleteAllocation: (id: string) => req(`/allocations/${id}`, { method: 'DELETE' }),

  getSettings: () => req<{ settings: Settings }>('/settings').then((r) => r.settings),
  updateSettings: (patch: Partial<Omit<Settings, 'id'>>) => req<{ settings: Settings }>('/settings', { method: 'PATCH', body: JSON.stringify(patch) }).then((r) => r.settings),
};
